import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, XCircle, Clock, FileCheck, Info } from "lucide-react";

// Doc types alinhados com ALLOWED_DOC_TYPES do backend
const DOC_TIPO_LABELS = {
  cpf: "CPF",
  rg: "RG",
  cnh: "CNH",
  comprovante_residencia: "Comprovante de Residência",
  cadunico: "CadÚnico",
  certidao_casamento: "Certidão de Casamento",
  certidao_nascimento: "Certidão de Nascimento (dependentes)",
  laudo_pcd: "Laudo PCD",
  outros: "Outros",
};

/**
 * Monta a lista de documentos exigidos considerando o perfil familiar da inscrição.
 * Retorna [{tipo, label, obrigatorio, condicao, enviado, docs}]
 */
function buildChecklist(insc) {
  const estadoCivil = (insc.estado_civil || "").toLowerCase();
  const casado = estadoCivil === "casado" || estadoCivil === "união estável" || estadoCivil === "uniao estavel";
  const temPcd = !!insc.possui_pcd;
  const temDependentes = (insc.qtd_dependentes || 0) > 0;
  const temCnh = !!(insc.cnh || insc.conjuge_cnh); // raramente usado — opcional

  const docsPorTipo = (insc.documentos || []).reduce((acc, d) => {
    (acc[d.tipo] = acc[d.tipo] || []).push(d);
    return acc;
  }, {});

  const items = [
    { tipo: "cpf", obrigatorio: true, condicao: "Sempre obrigatório" },
    { tipo: "rg", obrigatorio: true, condicao: "Sempre obrigatório (ou CNH)" },
    { tipo: "comprovante_residencia", obrigatorio: true, condicao: "Sempre obrigatório" },
    { tipo: "cadunico", obrigatorio: true, condicao: "Sempre obrigatório" },
    { tipo: "certidao_casamento", obrigatorio: casado, condicao: casado ? "Obrigatório (titular casado(a))" : "Opcional (titular solteiro(a))" },
    { tipo: "certidao_nascimento", obrigatorio: temDependentes, condicao: temDependentes ? "Obrigatório (há dependentes)" : "Opcional (sem dependentes)" },
    { tipo: "laudo_pcd", obrigatorio: temPcd, condicao: temPcd ? "Obrigatório (PCD declarado)" : "Opcional (sem PCD)" },
    { tipo: "cnh", obrigatorio: false, condicao: temCnh ? "Opcional (CNH informada)" : "Opcional" },
  ];

  return items.map((it) => {
    const docs = docsPorTipo[it.tipo] || [];
    return {
      ...it,
      label: DOC_TIPO_LABELS[it.tipo] || it.tipo,
      enviado: docs.length > 0,
      docs,
    };
  });
}

function statusLine(it) {
  if (it.enviado) {
    return <span className="text-xs text-emerald-700 font-medium">Enviado — {it.docs.length} arquivo(s)</span>;
  }
  if (it.obrigatorio) {
    return <span className="text-xs text-red-600 font-medium">Pendente</span>;
  }
  return <span className="text-xs text-slate-500">Opcional — não enviado</span>;
}

function iconFor(it) {
  if (it.enviado) return <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />;
  if (it.obrigatorio) return <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />;
  return <Clock className="w-5 h-5 text-slate-400 flex-shrink-0" />;
}

export default function DocumentosChecklist({ inscricao }) {
  if (!inscricao) return null;
  const items = buildChecklist(inscricao);
  const obrigatorios = items.filter((i) => i.obrigatorio);
  const obrigatoriosEnviados = obrigatorios.filter((i) => i.enviado).length;
  const totalObrigatorios = obrigatorios.length;
  const pct = totalObrigatorios ? Math.round((obrigatoriosEnviados / totalObrigatorios) * 100) : 100;
  const faltam = obrigatorios.filter((i) => !i.enviado);
  const prontoParaHomologar = faltam.length === 0;

  return (
    <Card data-testid="docs-checklist" className={prontoParaHomologar ? "border-emerald-200" : "border-amber-200"}>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div className="min-w-0">
          <CardTitle className="text-base text-[#083CA6] flex items-center gap-2">
            <FileCheck className="w-4 h-4" /> Checklist de Documentos
          </CardTitle>
          <CardDescription>
            {prontoParaHomologar
              ? "Todos os documentos obrigatórios foram enviados — pronto para homologação."
              : `Faltam ${faltam.length} documento(s) obrigatório(s) para completar.`}
          </CardDescription>
        </div>
        <Badge
          className={prontoParaHomologar
            ? "bg-emerald-100 text-emerald-800 border-emerald-300"
            : "bg-amber-100 text-amber-800 border-amber-300"}
          data-testid="checklist-pct"
        >
          {obrigatoriosEnviados}/{totalObrigatorios} obrigatórios
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <Progress value={pct} className="h-2" />

        <ul className="space-y-2">
          {items.map((it) => {
            let itemBorder = "border-slate-100";
            if (it.enviado) itemBorder = "border-emerald-100 bg-emerald-50/30";
            else if (it.obrigatorio) itemBorder = "border-red-100 bg-red-50/30";
            return (
            <li
              key={it.tipo}
              className={`flex items-start gap-3 p-2 rounded border ${itemBorder}`}
              data-testid={`checklist-item-${it.tipo}`}
            >
              {iconFor(it)}
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-2">
                  <span className="font-semibold text-sm">{it.label}</span>
                  {it.obrigatorio && (
                    <Badge className="bg-red-100 text-red-700 text-[10px] border-red-200">Obrigatório</Badge>
                  )}
                </div>
                <div className="text-xs text-slate-500">{it.condicao}</div>
                <div className="mt-1">{statusLine(it)}</div>
              </div>
            </li>
            );
          })}
        </ul>

        {faltam.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs flex items-start gap-2">
            <Info className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Para homologar</strong>, envie: {faltam.map((f) => f.label).join(", ")}.
              Use a tela "Editar Inscrição" para gerenciar os anexos.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
